/** Word-editor-style preview mock — full A4 page in the editor canvas. */
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  Underline,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";

const CANVAS: CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: "#ECEEF1",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

/** Portrait A4 proportions (210 × 297 mm). */
const A4_PAGE: CSSProperties = {
  height: "100%",
  aspectRatio: "210 / 297",
  maxWidth: "100%",
  background: "#fff",
  boxShadow:
    "0 2px 12px rgba(11,15,25,0.12), 0 8px 28px -8px rgba(11,15,25,0.18)",
  padding: "36px 32px",
  boxSizing: "border-box",
  overflow: "hidden",
};

/** Line numbers in the narrow editor gutter left of the page. */
const GUTTER_LINE_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export function PreviewWordMock(): ReactNode {
  const t = useTranslations("marketing.moduleDokumentePreviewWord");
  const toolBtn = (icon: ReactNode): ReactNode => (
    <span
      style={{ display: "inline-flex", padding: "3px 5px", color: H.inkDim }}
    >
      {icon}
    </span>
  );
  return (
    <div style={CANVAS}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "6px 10px",
          background: "#F5F6F8",
          borderBottom: "1px solid #E0E3E7",
          fontSize: 11,
          color: H.inkSoft,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            padding: "2px 8px",
            border: "1px solid #D8DCE2",
            borderRadius: 4,
          }}
        >
          {t("text")}
        </span>
        <span
          style={{
            padding: "2px 6px",
            border: "1px solid #D8DCE2",
            borderRadius: 4,
          }}
        >
          13
        </span>
        {toolBtn(<Bold width={13} height={13} strokeWidth={2} />)}
        {toolBtn(<Italic width={13} height={13} strokeWidth={2} />)}
        {toolBtn(<Underline width={13} height={13} strokeWidth={2} />)}
        <span
          style={{
            width: 1,
            height: 16,
            background: "#D8DCE2",
            margin: "0 4px",
          }}
        />
        {toolBtn(<AlignLeft width={13} height={13} strokeWidth={1.8} />)}
        {toolBtn(<AlignCenter width={13} height={13} strokeWidth={1.8} />)}
        {toolBtn(<AlignRight width={13} height={13} strokeWidth={1.8} />)}
        {toolBtn(<List width={13} height={13} strokeWidth={1.8} />)}
        {toolBtn(<ListOrdered width={13} height={13} strokeWidth={1.8} />)}
        <span style={{ marginLeft: "auto", fontSize: 10, color: H.inkGhost }}>
          {t("kopfFusszeile")}
        </span>
      </div>
      <div
        style={{
          height: 14,
          background: "#F8F9FA",
          borderBottom: "1px solid #E0E3E7",
          display: "flex",
          paddingLeft: 28,
          fontSize: 8,
          color: H.inkGhost,
          alignItems: "flex-end",
          flexShrink: 0,
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => (
          <span key={n} style={{ width: 18, textAlign: "center" }}>
            {n}
          </span>
        ))}
      </div>
      <div
        style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}
      >
        <div
          style={{
            width: 20,
            background: "#F8F9FA",
            borderRight: "1px solid #E0E3E7",
            fontSize: 8,
            color: H.inkGhost,
            paddingTop: 8,
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          {GUTTER_LINE_NUMBERS.map((n) => (
            <span key={n} style={{ display: "block" }}>
              {n}
            </span>
          ))}
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            justifyContent: "center",
            padding: "10px 14px",
            boxSizing: "border-box",
          }}
        >
          <div style={A4_PAGE}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: H.ink,
                marginBottom: 10,
              }}
            >
              {t("uebergabeprotokollWe12")}
            </div>
            <div style={{ fontSize: 11.5, color: H.inkSoft, lineHeight: 1.65 }}>
              {t.rich("uebergabeprotokollText", { br: () => <br /> })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
